// src/pages/consolidated/LoyerAnalysis.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Button,
  Card,
  Col,
  Row,
  Table,
  Badge,
  ProgressBar,
  ButtonGroup,
  Spinner,
  Dropdown,
  Container,
  Alert,
  Placeholder,
  Form
} from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import {
  House,
  HouseFill,
  Building,
  CurrencyDollar,
  People,
  GraphUp,
  Clock,
  Calendar,
  ArrowUp,
  ArrowDown,
  Cash,
  CheckCircle,
  Activity,
  BarChart,
  Globe,
  ExclamationCircle,
  ClockHistory,
  Repeat,
  Shop,
  Download,
  FileEarmarkExcel,
  FilePdf,
  Bullseye,
  Layers,
  Search,
  Info,
  Percent,
  GeoAlt,
  DoorOpen,
  Key,
  Wallet
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";

export default function LoyerAnalysis() {
  const defaultCurrency = sessionStorage.getItem("default_currency") || 'XOF';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState("month");
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [exportLoading, setExportLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('performance');

  const [dashboardData, setDashboardData] = useState({
    markets: [],
    globalStats: null,
    loyerStats: null
  });

  // ✅ Notification Toast
  const showNotification = (message, type = 'success') => {
    let toastContainer = document.getElementById('toast-container');
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.id = 'toast-container';
      toastContainer.className = 'position-fixed top-0 end-0 p-3';
      toastContainer.style.zIndex = '9999';
      toastContainer.style.maxWidth = '450px';
      document.body.appendChild(toastContainer);
    }

    const configs = {
      success: { icon: '✅', title: 'Succès' },
      danger: { icon: '❌', title: 'Erreur' },
      warning: { icon: '⚠️', title: 'Attention' },
      info: { icon: 'ℹ️', title: 'Information' }
    };

    const config = configs[type] || configs.info;
    const toast = document.createElement('div');
    toast.className = `toast align-items-center border-0 show shadow-lg`;
    toast.style.cssText = `
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      background: white;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.15);
      border-left: 5px solid ${type === 'success' ? '#28a745' : type === 'danger' ? '#dc3545' : type === 'warning' ? '#ffc107' : '#17a2b8'};
      min-width: 280px;
    `;

    toast.innerHTML = `
      <div class="d-flex align-items-center p-2">
        <div class="flex-shrink-0 ms-2">
          <div style="width: 40px; height: 40px; border-radius: 50%; background: ${type === 'success' ? '#d4edda' : type === 'danger' ? '#f8d7da' : type === 'warning' ? '#fff3cd' : '#d1ecf1'}; display: flex; align-items: center; justify-content: center; font-size: 20px;">
            ${config.icon}
          </div>
        </div>
        <div class="flex-grow-1 ms-3">
          <div class="fw-bold" style="font-size: 13px;">${config.title}</div>
          <div style="font-size: 12px; color: #555;">${message}</div>
        </div>
        <button type="button" class="btn-close me-2" style="font-size: 10px;"></button>
      </div>
    `;

    toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(0)';
    }, 50);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => toast.remove(), 500);
    }, 5000);

    toast.querySelector('.btn-close').addEventListener('click', () => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => toast.remove(), 500);
    });
  };

  const getAuthToken = () => sessionStorage.getItem("authToken");
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return numAmount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + defaultCurrency;
  };

  const loadDashboardData = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) setRefreshLoading(true);
      else setLoading(true);
      setError(null);

      const params = {
        time_range: timeRange,
        market_id: selectedMarket !== 'all' ? selectedMarket : undefined
      };

      const response = await axios.get(`${API_BASE_URL}/consolidated/dashboard`, {
        headers: getHeaders(),
        params
      });

      if (response.data.success) {
        const data = response.data.data;
        setDashboardData({
          markets: data.markets || [],
          globalStats: data.global_stats || null,
          loyerStats: data.loyer_stats || null
        });
      } else {
        setError(response.data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error("Erreur chargement:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
      setRefreshLoading(false);
    }
  }, [timeRange, selectedMarket]);

  const handleTimeRangeChange = (newRange) => {
    if (newRange === timeRange) return;
    setTimeRange(newRange);
  };

  // ✅ Export PDF
  const handleExportPDF = () => {
    if (exportLoading) return;
    setExportLoading(true);

    try {
      const fileName = `ANALYSE_LOYERS_${new Date().toISOString().split('T')[0]}`;
      const originalTitle = document.title;
      document.title = fileName;

      const formatCurrencyPDF = (amount) => {
        const num = parseFloat(amount) || 0;
        return num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + defaultCurrency;
      };

      const loyer = dashboardData.loyerStats || {};

      const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${fileName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; padding: 4mm 5mm; background: white; font-size: 9pt; line-height: 1.25; color: #1a1a1a; }
    .report { max-width: 100%; margin: 0 auto; }
    .header { text-align: center; padding-bottom: 4px; border-bottom: 2px solid #1a1a1a; margin-bottom: 10px; }
    .header h1 { font-size: 14pt; font-weight: bold; letter-spacing: 2px; text-transform: uppercase; margin: 1px 0; }
    .header .subtitle { font-size: 11pt; color: #2a2a2a; margin-top: 1px; }
    .header .meta { font-size: 7.5pt; color: #555; margin-top: 2px; display: flex; justify-content: center; gap: 15px; }
    .section { margin-bottom: 8px; }
    .section-title { font-size: 9.5pt; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #999; padding-bottom: 2px; margin-bottom: 5px; }
    .stats-grid { display: table; width: 100%; border-collapse: collapse; }
    .stats-row { display: table-row; }
    .stat-item { display: table-cell; padding: 2px 4px; border-bottom: 1px solid #eee; vertical-align: middle; }
    .stat-item .label { font-size: 8pt; color: #444; }
    .stat-item .value { font-size: 9.5pt; font-weight: bold; text-align: right; }
    .stat-item .value.green { color: #1a7a3a; }
    .stat-item .value.blue { color: #1a3a6a; }
    .stat-item .value.orange { color: #b85a1a; }
    .stat-item .value.red { color: #8a1a1a; }
    table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 2px; }
    table thead th { background: #f0f0f0; padding: 3px 5px; text-align: left; font-weight: bold; border-bottom: 2px solid #666; font-size: 7pt; text-transform: uppercase; }
    table tbody td { padding: 2px 5px; border-bottom: 1px solid #e8e8e8; font-size: 8pt; }
    table tbody tr:nth-child(even) { background: #f8f8f8; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .fw-bold { font-weight: bold; }
    .badge { display: inline-block; padding: 1px 6px; border: 1px solid #666; font-size: 7.5pt; font-weight: bold; }
    .badge.success { border-color: #1a7a3a; color: #1a7a3a; }
    .badge.warning { border-color: #b85a1a; color: #b85a1a; }
    .badge.danger { border-color: #8a1a1a; color: #8a1a1a; }
    .footer { margin-top: 12px; padding-top: 4px; border-top: 1px solid #ccc; font-size: 7pt; color: #777; display: flex; justify-content: space-between; }
    .loyer-header { background: linear-gradient(135deg, #f0f7ff 0%, #e6f2ff 100%); padding: 8px; border-left: 4px solid #0d6efd; margin-bottom: 8px; }
  </style>
</head>
<body>
  <div class="report">
    <div class="header">
      <h1> ANALYSE DES LOYERS</h1>
      <div class="subtitle">VUE D'ENSEMBLE DÉTAILLÉE</div>
      <div class="meta">
        <span>Période : ${timeRange === 'week' ? 'Cette semaine' : timeRange === 'month' ? 'Ce mois' : 'Cette année'}</span>
        <span>|</span>
        <span>${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
        <span>|</span>
        <span>${dashboardData.markets.length} marchés</span>
      </div>
    </div>

    <div class="loyer-header">
      <div class="section-title"> ${loyer.taxe_nom || 'Taxe sur les Loyers'}</div>
    </div>

    <div class="section">
      <div class="stats-grid">
        <div class="stats-row">
          <div class="stat-item"><span class="label">Total attendu</span></div>
          <div class="stat-item"><span class="value">${formatCurrencyPDF(loyer.total_attendu || 0)}</span></div>
          <div class="stat-item"><span class="label">Total collecté</span></div>
          <div class="stat-item"><span class="value green">${formatCurrencyPDF(loyer.total_collecte || 0)}</span></div>
        </div>
        <div class="stats-row">
          <div class="stat-item"><span class="label">Taux de recouvrement</span></div>
          <div class="stat-item"><span class="value ${(loyer.taux_recouvrement || 0) >= 80 ? 'green' : (loyer.taux_recouvrement || 0) >= 50 ? 'orange' : 'red'}">${loyer.taux_recouvrement || 0}%</span></div>
          <div class="stat-item"><span class="label">Contribuables</span></div>
          <div class="stat-item"><span class="value blue">${loyer.nombre_contribuables || 0}</span></div>
        </div>
        <div class="stats-row">
          <div class="stat-item"><span class="label">Paiements effectués</span></div>
          <div class="stat-item"><span class="value">${loyer.nombre_paiements || 0}</span></div>
          <div class="stat-item"><span class="label">Écart (Attendu - Collecté)</span></div>
          <div class="stat-item"><span class="value ${((loyer.total_attendu || 0) - (loyer.total_collecte || 0)) <= 0 ? 'green' : 'red'}">${formatCurrencyPDF((loyer.total_attendu || 0) - (loyer.total_collecte || 0))}</span></div>
        </div>
      </div>
    </div>

    ${loyer.par_marche && loyer.par_marche.length > 0 ? `
    <div class="section">
      <div class="section-title">🏪 Répartition par Marché</div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Marché</th>
            <th class="text-right">Collecté</th>
            <th class="text-right">Attendu</th>
            <th class="text-right">Taux (%)</th>
            <th class="text-center">Statut</th>
          </tr>
        </thead>
        <tbody>
          ${loyer.par_marche.map((item, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td><strong>${item.marche_nom}</strong></td>
              <td class="text-right">${formatCurrencyPDF(item.collecte)}</td>
              <td class="text-right">${formatCurrencyPDF(item.attendu)}</td>
              <td class="text-right">
                <span class="badge ${item.taux_recouvrement >= 80 ? 'success' : item.taux_recouvrement >= 50 ? 'warning' : 'danger'}">
                  ${item.taux_recouvrement}%
                </span>
              </td>
              <td class="text-center">${item.taux_recouvrement >= 80 ? '✅ Atteint' : item.taux_recouvrement >= 50 ? '⚠️ En cours' : '❌ À améliorer'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
    ` : ''}

    ${loyer.evolution && loyer.evolution.length > 0 ? `
    <div class="section">
      <div class="section-title">📈 Évolution des Collectes</div>
      <table>
        <thead>
          <tr>
            <th>Mois</th>
            <th class="text-right">Collecte</th>
          </tr>
        </thead>
        <tbody>
          ${loyer.evolution.map(item => `
            <tr>
              <td>${item.mois}</td>
              <td class="text-right">${formatCurrencyPDF(item.collecte)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
    ` : ''}

    <div class="footer">
      <span>Généré par SyncTaxe</span>
      <span>${new Date().toLocaleDateString('fr-FR')} • ${new Date().toLocaleTimeString('fr-FR')}</span>
    </div>
  </div>
</body>
</html>`;

      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:none;';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      iframe.contentWindow.onload = () => {
        iframe.contentWindow.print();
        setTimeout(() => {
          document.title = originalTitle;
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          setExportLoading(false);
          showNotification('PDF exporté avec succès ✅', 'success');
        }, 1500);
      };
    } catch (err) {
      console.error('Erreur export PDF:', err);
      showNotification('Erreur lors de l\'export PDF ❌', 'danger');
      setExportLoading(false);
    }
  };

  // ✅ Export Excel
  const handleExportExcel = () => {
    if (exportLoading) return;
    setExportLoading(true);

    try {
      const wb = XLSX.utils.book_new();
      const loyer = dashboardData.loyerStats || {};

      const resumeData = [
        [' ANALYSE DES LOYERS'],
        [''],
        ['Période', timeRange === 'week' ? 'Cette semaine' : timeRange === 'month' ? 'Ce mois' : 'Cette année'],
        ['Date de génération', new Date().toLocaleDateString('fr-FR')],
        [''],
        ['INDICATEURS CLÉS'],
        ['Indicateur', 'Valeur'],
        ['Total attendu', loyer.total_attendu || 0],
        ['Total collecté', loyer.total_collecte || 0],
        ['Taux de recouvrement', (loyer.taux_recouvrement || 0) + '%'],
        ['Nombre de contribuables', loyer.nombre_contribuables || 0],
        ['Nombre de paiements', loyer.nombre_paiements || 0],
        ['Écart (Attendu - Collecté)', (loyer.total_attendu || 0) - (loyer.total_collecte || 0)]
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(resumeData);
      ws1['!cols'] = [{ wch: 30 }, { wch: 30 }];
      ws1['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Résumé');

      if (loyer.par_marche && loyer.par_marche.length > 0) {
        const repartitionData = [
          ['RÉPARTITION PAR MARCHÉ'],
          [''],
          ['#', 'Marché', 'Collecté', 'Attendu', 'Taux (%)', 'Statut'],
          ...loyer.par_marche.map((item, idx) => [
            idx + 1,
            item.marche_nom,
            item.collecte || 0,
            item.attendu || 0,
            item.taux_recouvrement || 0,
            item.taux_recouvrement >= 80 ? 'Atteint' : item.taux_recouvrement >= 50 ? 'En cours' : 'À améliorer'
          ])
        ];

        const ws2 = XLSX.utils.aoa_to_sheet(repartitionData);
        ws2['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 20 }];
        ws2['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Répartition');
      }

      if (loyer.evolution && loyer.evolution.length > 0) {
        const evolutionData = [
          ['ÉVOLUTION DES COLLECTES'],
          [''],
          ['Mois', 'Collecte'],
          ...loyer.evolution.map(item => [item.mois, item.collecte || 0])
        ];

        const ws3 = XLSX.utils.aoa_to_sheet(evolutionData);
        ws3['!cols'] = [{ wch: 20 }, { wch: 25 }];
        ws3['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
        XLSX.utils.book_append_sheet(wb, ws3, 'Évolution');
      }

      XLSX.writeFile(wb, `ANALYSE_LOYERS_${new Date().toISOString().split('T')[0]}.xlsx`);
      showNotification('Excel exporté avec succès ✅', 'success');
    } catch (err) {
      console.error('Erreur export Excel:', err);
      showNotification('Erreur lors de l\'export Excel ❌', 'danger');
    } finally {
      setExportLoading(false);
    }
  };

  // ✅ Configuration des graphiques
  const loyerEvolutionOptions = useMemo(() => ({
    chart: {
      type: 'area',
      height: 200,
      toolbar: { show: true, tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
      background: 'transparent',
      animations: { enabled: true, easing: 'easeinout', speed: 800 }
    },
    colors: ['#0d6efd', '#2ecc71'],
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 0.5, opacityFrom: 0.8, opacityTo: 0.1, stops: [0, 100] }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val.toLocaleString('fr-FR'),
      style: { fontSize: '9px', fontWeight: 'bold', colors: ['#2c3e50'] }
    },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: {
      categories: dashboardData.loyerStats?.evolution?.map(e => e.mois) || [],
      labels: { style: { colors: '#6c757d', fontSize: '10px' }, rotate: 0 }
    },
    yaxis: {
      labels: { formatter: (value) => value.toLocaleString('fr-FR'), style: { colors: '#6c757d', fontSize: '10px' } }
    },
    tooltip: {
      y: { formatter: (value) => `${value.toLocaleString('fr-FR')} ${defaultCurrency}`, title: { formatter: () => `<strong>Loyers</strong>` } },
      shared: true,
      intersect: false
    },
    legend: { position: 'top', horizontalAlign: 'center', fontSize: '12px', fontWeight: 600 },
    grid: { borderColor: '#e0e6ed', strokeDashArray: 5 }
  }), [dashboardData.loyerStats, defaultCurrency]);

  const loyerSeries = useMemo(() => [
    { name: 'Collecté', data: dashboardData.loyerStats?.evolution?.map(e => e.collecte) || [] }
  ], [dashboardData.loyerStats]);

  const distributionChartOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      height: 280,
      toolbar: { show: true, tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
      background: 'transparent',
      animations: { enabled: true, easing: 'easeinout', speed: 800 }
    },
    colors: ['#2ecc71', '#0d6efd'],
    plotOptions: {
      bar: { borderRadius: 0, horizontal: true, columnWidth: '60%', dataLabels: { position: 'top' } }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val.toLocaleString('fr-FR'),
      style: { fontSize: '10px', fontWeight: 'bold', colors: ['#2c3e50'] }
    },
    xaxis: {
      categories: dashboardData.loyerStats?.par_marche?.map(item => item.marche_nom) || [],
      labels: { style: { colors: '#2c3e50', fontSize: '11px', fontWeight: 500 }, rotate: 0 }
    },
    yaxis: {
      labels: { formatter: (value) => value.toLocaleString('fr-FR'), style: { colors: '#6c757d', fontSize: '10px' } }
    },
    tooltip: {
      y: { formatter: (value) => `${value.toLocaleString('fr-FR')} ${defaultCurrency}`, title: { formatter: (s) => `<strong>${s}</strong>` } },
      shared: true,
      intersect: false
    },
    legend: { position: 'top', horizontalAlign: 'center', fontSize: '12px', fontWeight: 600 },
    grid: { borderColor: '#e0e6ed', strokeDashArray: 5 }
  }), [dashboardData.loyerStats, defaultCurrency]);

  const distributionSeries = useMemo(() => [
    { name: 'Collecté', data: dashboardData.loyerStats?.par_marche?.map(item => item.collecte) || [] },
    { name: 'Attendu', data: dashboardData.loyerStats?.par_marche?.map(item => item.attendu) || [] }
  ], [dashboardData.loyerStats]);

  const SkeletonLoyer = () => (
    <Card className="border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f0f7ff 0%, #e6f2ff 100%)', borderLeft: '4px solid #0d6efd' }}>
      <Card.Header className="bg-transparent border-bottom py-3">
        <Placeholder as="div" animation="glow">
          <Placeholder xs={8} style={{ height: '24px' }} />
          <Placeholder xs={4} style={{ height: '14px' }} className="mt-1" />
        </Placeholder>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col lg={6}>
            <Row className="g-2">
              {[...Array(4)].map((_, i) => (
                <Col sm={6} key={i}>
                  <div className="p-3 bg-white rounded-3 shadow-sm">
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={6} style={{ height: '12px' }} />
                      <Placeholder xs={8} style={{ height: '24px' }} className="mt-1" />
                    </Placeholder>
                  </div>
                </Col>
              ))}
            </Row>
          </Col>
          <Col lg={6}>
            <div className="p-3 bg-white rounded-3 shadow-sm h-100">
              <Placeholder as="div" animation="glow">
                <Placeholder xs={4} style={{ height: '16px' }} className="mb-3" />
              </Placeholder>
              <div className="bg-light rounded-3" style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner animation="border" variant="secondary" size="sm" />
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  useEffect(() => {
    loadDashboardData();
  }, [timeRange, selectedMarket]);

  // Filtrer et trier
  const filteredMarkets = useMemo(() => {
    let markets = dashboardData.loyerStats?.par_marche || [];

    if (searchTerm) {
      markets = markets.filter(item =>
        item.marche_nom.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      if (filterStatus === 'good') markets = markets.filter(item => item.taux_recouvrement >= 80);
      else if (filterStatus === 'medium') markets = markets.filter(item => item.taux_recouvrement >= 50 && item.taux_recouvrement < 80);
      else if (filterStatus === 'bad') markets = markets.filter(item => item.taux_recouvrement < 50);
    }

    switch (sortBy) {
      case 'performance': markets.sort((a, b) => b.taux_recouvrement - a.taux_recouvrement); break;
      case 'collecte': markets.sort((a, b) => b.collecte - a.collecte); break;
      case 'attendu': markets.sort((a, b) => b.attendu - a.attendu); break;
      case 'ecart': markets.sort((a, b) => (b.attendu - b.collecte) - (a.attendu - a.collecte)); break;
      default: break;
    }

    return markets;
  }, [dashboardData.loyerStats, searchTerm, filterStatus, sortBy]);

  const loyer = dashboardData.loyerStats || {};
  const totalMarkets = dashboardData.markets.length;
  const totalContribuables = dashboardData.globalStats?.total_contribuables || 0;
  const totalCollecte = loyer.total_collecte || 0;
  const totalAttendu = loyer.total_attendu || 0;
  const tauxRecouvrement = loyer.taux_recouvrement || 0;
  const ecart = totalAttendu - totalCollecte;

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <Container fluid>
          {/* En-tête */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary bg-opacity-10 p-3 rounded-3">
                  <House size={32} className="text-primary" />
                </div>
                <div>
                  <h4 className="main-title mb-0 fw-bold" style={{ fontSize: '24px' }}>
                    Analyse des Loyers
                  </h4>
                  <p className="text-muted mb-0 d-flex align-items-center gap-2">
                    <Calendar size={14} />
                    Vue détaillée de la performance des loyers
                    <span className="text-muted">•</span>
                    {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap mt-3 mt-md-0">
              <ButtonGroup size="sm" className="shadow-sm">
                <Button
                  variant={timeRange === "week" ? "primary" : "outline-primary"}
                  onClick={() => handleTimeRangeChange("week")}
                  className="px-3"
                  disabled={loading}
                >
                  Semaine
                </Button>
                <Button
                  variant={timeRange === "month" ? "primary" : "outline-primary"}
                  onClick={() => handleTimeRangeChange("month")}
                  className="px-3"
                  disabled={loading}
                >
                  Mois
                </Button>
                <Button
                  variant={timeRange === "year" ? "primary" : "outline-primary"}
                  onClick={() => handleTimeRangeChange("year")}
                  className="px-3"
                  disabled={loading}
                >
                  Année
                </Button>
              </ButtonGroup>

              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm" className="shadow-sm" disabled={exportLoading || loading}>
                  {exportLoading ? <Spinner animation="border" size="sm" /> : <Download size={14} className="me-1" />}
                  Exporter
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleExportPDF} disabled={exportLoading || loading}>
                    <FilePdf className="me-2 text-danger" size={16} /> PDF
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleExportExcel} disabled={exportLoading || loading}>
                    <FileEarmarkExcel className="me-2 text-success" size={16} /> Excel
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <Button
                variant="outline-secondary"
                size="sm"
                className="shadow-sm"
                onClick={() => loadDashboardData(true)}
                disabled={refreshLoading || loading}
              >
                {refreshLoading ? <Spinner animation="border" size="sm" /> : <ClockHistory size={16} />}
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="border-0 shadow-sm d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <ExclamationCircle size={24} className="me-2" />
                <span className="fw-medium">{error}</span>
              </div>
              <Button variant="outline-danger" size="sm" onClick={() => loadDashboardData(true)}>
                <Repeat className="me-1" size={14} /> Réessayer
              </Button>
            </Alert>
          )}

          {/* Section principale Loyers */}
          {loading ? (
            <SkeletonLoyer />
          ) : (
            <>
              {/* Statistiques globales */}
              <Row className="g-3 mb-4">
                <Col md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100 stats-card">
                    <div className="position-absolute top-0 start-0 w-100 bg-primary" style={{ height: '4px' }} />
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="rounded-3 p-2 me-3" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                          <House className="text-primary" size={24} />
                        </div>
                        <div>
                          <h5 className="fw-bold mb-0">{formatCurrency(totalAttendu)}</h5>
                          <span className="text-muted small fw-medium">Total attendu</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100 stats-card">
                    <div className="position-absolute top-0 start-0 w-100 bg-success" style={{ height: '4px' }} />
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="rounded-3 p-2 me-3" style={{ backgroundColor: 'rgba(46, 204, 113, 0.1)' }}>
                          <Cash className="text-success" size={24} />
                        </div>
                        <div>
                          <h5 className="fw-bold mb-0">{formatCurrency(totalCollecte)}</h5>
                          <span className="text-muted small fw-medium">Total collecté</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100 stats-card">
                    <div className={`position-absolute top-0 start-0 w-100 ${tauxRecouvrement >= 80 ? 'bg-success' : tauxRecouvrement >= 50 ? 'bg-warning' : 'bg-danger'}`} style={{ height: '4px' }} />
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="rounded-3 p-2 me-3" style={{ backgroundColor: 'rgba(241, 196, 15, 0.1)' }}>
                          <Percent className="text-warning" size={24} />
                        </div>
                        <div>
                          <h5 className="fw-bold mb-0">{tauxRecouvrement}%</h5>
                          <span className="text-muted small fw-medium">Taux de recouvrement</span>
                        </div>
                      </div>
                      <ProgressBar
                        now={tauxRecouvrement}
                        variant={tauxRecouvrement >= 80 ? "success" : tauxRecouvrement >= 50 ? "warning" : "danger"}
                        style={{ height: '4px', borderRadius: '2px' }}
                        className="mt-2"
                      />
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100 stats-card">
                    <div className={`position-absolute top-0 start-0 w-100 ${ecart <= 0 ? 'bg-success' : 'bg-danger'}`} style={{ height: '4px' }} />
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="rounded-3 p-2 me-3" style={{ backgroundColor: ecart <= 0 ? 'rgba(46, 204, 113, 0.1)' : 'rgba(231, 76, 60, 0.1)' }}>
                          {ecart <= 0 ? <ArrowUp className="text-success" size={24} /> : <ArrowDown className="text-danger" size={24} />}
                        </div>
                        <div>
                          <h5 className={`fw-bold mb-0 ${ecart <= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatCurrency(Math.abs(ecart))}
                          </h5>
                          <span className="text-muted small fw-medium">
                            {ecart <= 0 ? 'Excédent' : 'Déficit'}
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Graphiques */}
              <Row className="g-4 mb-4">
                <Col lg={7}>
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom py-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <Card.Title as="h6" className="mb-0 fw-semibold">
                            <BarChart className="me-2 text-primary" size={18} />
                            Répartition par Marché
                          </Card.Title>
                          <small className="text-muted">Comparaison collecté vs attendu</small>
                        </div>
                        <Badge bg="primary" className="rounded-pill px-3 py-2">
                          {filteredMarkets.length} marchés
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {filteredMarkets.length > 0 ? (
                        <>
                          <ReactApexChart
                            options={distributionChartOptions}
                            series={distributionSeries}
                            type="bar"
                            height={300}
                          />
                          <div className="mt-2 d-flex justify-content-center gap-4">
                            <div className="d-flex align-items-center">
                              <span className="d-inline-block rounded-circle me-2" style={{ width: '12px', height: '12px', backgroundColor: '#2ecc71' }} />
                              <small className="text-muted">Collecté</small>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="d-inline-block rounded-circle me-2" style={{ width: '12px', height: '12px', backgroundColor: '#0d6efd' }} />
                              <small className="text-muted">Attendu</small>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <ExclamationCircle size={48} className="text-muted opacity-25 mb-3" />
                          <p className="text-muted">Aucune donnée disponible</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={5}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Header className="bg-white border-bottom py-3">
                      <Card.Title as="h6" className="mb-0 fw-semibold">
                        <Activity className="me-2 text-primary" size={18} />
                        Évolution des Collectes
                      </Card.Title>
                      <small className="text-muted">Tendance mensuelle</small>
                    </Card.Header>
                    <Card.Body>
                      {loyer.evolution && loyer.evolution.length > 0 ? (
                        <ReactApexChart
                          options={loyerEvolutionOptions}
                          series={loyerSeries}
                          type="area"
                          height={280}
                        />
                      ) : (
                        <div className="text-center py-4">
                          <ExclamationCircle size={48} className="text-muted opacity-25 mb-3" />
                          <p className="text-muted">Aucune donnée d'évolution</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Tableau détaillé */}
              <Row className="g-4">
                <Col lg={12}>
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between py-3">
                      <div>
                        <Card.Title as="h6" className="mb-0 fw-semibold">
                          <Layers className="me-2 text-primary" size={18} />
                          Détail par Marché
                        </Card.Title>
                        <small className="text-muted">Analyse détaillée de la performance des loyers</small>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <div className="d-flex align-items-center">
                          <Search size={16} className="text-muted me-1" />
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder="Rechercher un marché..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: '180px' }}
                          />
                        </div>
                        <Form.Select
                          size="sm"
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                          style={{ width: '150px' }}
                        >
                          <option value="all">Tous les statuts</option>
                          <option value="good">✅ Atteint (≥80%)</option>
                          <option value="medium">⚠️ En cours (50-79%)</option>
                          <option value="bad">❌ À améliorer (&lt;50%)</option>
                        </Form.Select>
                        <Form.Select
                          size="sm"
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          style={{ width: '150px' }}
                        >
                          <option value="performance">Trier par performance</option>
                          <option value="collecte">Trier par collecte</option>
                          <option value="attendu">Trier par attendu</option>
                          <option value="ecart">Trier par écart</option>
                        </Form.Select>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <Table hover responsive className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th className="py-3" style={{ width: '50px' }}>#</th>
                            <th className="py-3">Marché</th>
                            <th className="py-3 text-end">Collecté</th>
                            <th className="py-3 text-end">Attendu</th>
                            <th className="py-3 text-end">Écart</th>
                            <th className="py-3 text-end">Taux</th>
                            <th className="py-3 text-end">Paiements</th>
                            <th className="py-3 text-center">Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredMarkets.map((item, idx) => {
                            const ecartItem = item.attendu - item.collecte;
                            return (
                              <tr key={idx} className="align-middle">
                                <td className="fw-bold">{idx + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <House className="me-2 text-primary" size={16} />
                                    <span className="fw-medium">{item.marche_nom}</span>
                                  </div>
                                </td>
                                <td className="text-end text-success fw-bold">{formatCurrency(item.collecte)}</td>
                                <td className="text-end text-muted">{formatCurrency(item.attendu)}</td>
                                <td className={`text-end fw-bold ${ecartItem <= 0 ? 'text-success' : 'text-danger'}`}>
                                  {formatCurrency(Math.abs(ecartItem))}
                                  {ecartItem <= 0 ? ' ✅' : ' ⚠️'}
                                </td>
                                <td className="text-end">
                                  <div className="d-flex align-items-center justify-content-end">
                                    <ProgressBar
                                      now={item.taux_recouvrement}
                                      variant={item.taux_recouvrement >= 80 ? "success" : item.taux_recouvrement >= 50 ? "warning" : "danger"}
                                      style={{ width: '80px', height: '6px', borderRadius: '3px' }}
                                      className="me-2"
                                    />
                                    <span className={`fw-bold ${item.taux_recouvrement >= 80 ? 'text-success' : item.taux_recouvrement >= 50 ? 'text-warning' : 'text-danger'}`}>
                                      {item.taux_recouvrement}%
                                    </span>
                                  </div>
                                </td>
                                <td className="text-end">{item.nombre_paiements || 0}</td>
                                <td className="text-center">
                                  <Badge bg={item.taux_recouvrement >= 80 ? "success" : item.taux_recouvrement >= 50 ? "warning" : "danger"} className="rounded-pill px-3 py-2">
                                    {item.taux_recouvrement >= 80 ? '✅ Atteint' : item.taux_recouvrement >= 50 ? '⚠️ En cours' : '❌ À améliorer'}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                          {filteredMarkets.length === 0 && (
                            <tr>
                              <td colSpan="8" className="text-center py-4 text-muted">
                                <ExclamationCircle size={24} className="me-2" />
                                Aucun marché trouvé
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Résumé statistique */}
              <Row className="mt-4">
                <Col lg={12}>
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom py-3">
                      <Card.Title as="h6" className="mb-0 fw-semibold">
                        <Info className="me-2 text-primary" size={18} />
                        Résumé Statistique
                      </Card.Title>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={3}>
                          <div className="p-3 bg-light rounded-3 text-center">
                            <small className="text-muted d-block">Nombre de marchés</small>
                            <h4 className="fw-bold mb-0">{totalMarkets}</h4>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="p-3 bg-light rounded-3 text-center">
                            <small className="text-muted d-block">Contribuables</small>
                            <h4 className="fw-bold mb-0">{totalContribuables}</h4>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="p-3 bg-light rounded-3 text-center">
                            <small className="text-muted d-block">Performance moyenne</small>
                            <h4 className={`fw-bold mb-0 ${tauxRecouvrement >= 80 ? 'text-success' : tauxRecouvrement >= 50 ? 'text-warning' : 'text-danger'}`}>
                              {tauxRecouvrement}%
                            </h4>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="p-3 bg-light rounded-3 text-center">
                            <small className="text-muted d-block">Statut global</small>
                            <Badge bg={tauxRecouvrement >= 80 ? "success" : tauxRecouvrement >= 50 ? "warning" : "danger"} className="fs-6 px-3 py-2">
                              {tauxRecouvrement >= 80 ? '✅ Bonne performance' : tauxRecouvrement >= 50 ? '⚠️ En progrès' : '❌ À améliorer'}
                            </Badge>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          <Footer />
        </Container>
      </div>

      <style>{`
        .stats-card { transition: transform 0.3s ease, box-shadow 0.3s ease; border-radius: 12px; overflow: hidden; }
        .stats-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0,0,0,0.08) !important; }
        .table > :not(caption) > * > * { padding: 12px 16px; }
        .table-hover tbody tr:hover { background-color: rgba(13, 110, 253, 0.05); }
        .main-title { font-weight: 700; }
        .card { border-radius: 12px !important; }
        .shadow-sm { box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important; }
      `}</style>
    </React.Fragment>
  );
}